const faqLinkElement =
  '<div class="zola-text faq-link">Having trouble uploading photos? Check out our <a href="https://help.zola.com/hc/en-us/articles/360052051352-How-do-I-upload-photos-to-my-paper-products-" target="_blank">FAQs</a> or reach out at <a href="mailto:support@zola.com" target="_blank">support@zola.com</a></div>';

const DEFAULT_COPY = {
  uploading: `Hang tight! We're uploading your photos...`,
  errors: {
    dimensions: 'Images must be at least {minDimensionsCopy}.',
    size: 'Images must be under {maxSize}MB',
    fileType: 'Images must be {fileTypeCopy} files',
    image: 'Images must be {fileTypeCopy} files',
    user: `Please upload no more than {maxFileCount} images at once`,
  },
  dialog: {
    tabs: {
      file: {
        drag: 'Drag & drop here',
        or: 'or',
        button: 'Upload Your Files<span class="zola-text file-info">{fileRequirementsCopy}</span>',
      },
      names: {
        file: 'Your Files',
      },
      preview: {
        done: 'Save',
        error: {
          dimensions: {
            title: 'Looks like your photo is too small...',
            text: `Images must be at least {minDimensionsCopy}.${faqLinkElement}`,
            back: 'Select Another Photo',
          },
          size: {
            title: 'Looks like your photo is too big...',
            text: `Images must be under {maxSize}MB${faqLinkElement}`,
            back: 'Select Another Photo',
          },
          image: {
            title: 'Your selected file type cannot be uploaded…',
            text: `Images must be {fileTypeCopy} files${faqLinkElement}`,
            back: 'Select Another Photo',
          },
        },
        image: {
          title: 'Edit Photo',
        },
        unknown: {
          title: `Hang tight! We're uploading your photos...`,
        },
        multiple: {
          tooManyFiles:
            'What beautiful photos! Please upload no more than {maxFileCount} at a time.',
        },
      },
    },
  },
};

const FILE_TYPE_COPY = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/svg+xml': 'SVG',
  'image/tiff': 'TIFF',
  'image/webp': 'WEBP',
  'image/bmp': 'Bitmap',
};

/**
 * Recursively formats all strings contained in an object, replacing template
 * variables (in curly braces) with their corresponding values (if available).
 * @param {(Object|String)} currentNode - A translation object or string. Strings can contain template variables in curly braces
 * @param {Object} replacements - An object with properties for each template variable to replace
 * @returns {(Object|String)} - A formatted version of currentNode
 */
export const replaceTemplateVariables = (currentNode, replacements) => {
  if (typeof currentNode === 'string') {
    let formatted = currentNode;

    Object.keys(replacements).forEach(key => {
      formatted = formatted.replace(`{${key}}`, replacements[key]);
    });

    return formatted;
  }

  const newNode = {};

  Object.keys(currentNode).forEach(key => {
    newNode[key] = replaceTemplateVariables(currentNode[key], replacements);
  });

  return newNode;
};

const getMinDimensionsCopy = ({ minDimension, minWidth, minHeight }) => {
  let minDimensionsTemplate = '';

  if (minDimension) {
    minDimensionsTemplate = '{minDimension}px wide or high';
  } else if (minWidth && minHeight) {
    minDimensionsTemplate = '{minWidth} x {minHeight} pixels';
  } else if (minWidth) {
    minDimensionsTemplate = '{minWidth}px wide';
  } else if (minHeight) {
    minDimensionsTemplate = '{minHeight}px high';
  }

  return replaceTemplateVariables(minDimensionsTemplate, { minDimension, minWidth, minHeight });
};

// Returns a string enumerating all accepted file types
const getFileTypeCopy = ({ allowedFileTypes = [] }) => {
  // Empty
  if (!allowedFileTypes.length) return '';

  // Single
  if (allowedFileTypes.length === 1) return FILE_TYPE_COPY[allowedFileTypes[0]];

  // Multiples. Insert a comma between every item except the last, which uses the word "or".
  const fileTypeNames = allowedFileTypes.map(type => FILE_TYPE_COPY[type]);
  const allButLast = fileTypeNames.slice(0, -1);
  const last = fileTypeNames.slice(-1);
  return [allButLast.join(', '), ...last].join(' or ');
};

const getFileRequirementsCopy = ({ maxSize }, minDimensionsCopy, fileTypeCopy) => {
  if (!maxSize && !minDimensionsCopy && !fileTypeCopy) return '';

  const fileRequirementsTemplate = minDimensionsCopy
    ? 'Images ({fileTypeCopy}) must be at least<br>{minDimensionsCopy} and under {maxSize}MB each.'
    : 'Images ({fileTypeCopy}) must be under {maxSize}MB each.';

  return replaceTemplateVariables(fileRequirementsTemplate, {
    maxSize,
    minDimensionsCopy,
    fileTypeCopy,
  });
};

export const getWidgetCopy = values => {
  const minDimensionsCopy = getMinDimensionsCopy(values);
  const fileTypeCopy = getFileTypeCopy(values);
  const fileRequirementsCopy = getFileRequirementsCopy(values, minDimensionsCopy, fileTypeCopy);

  return replaceTemplateVariables(DEFAULT_COPY, {
    ...values,
    minDimensionsCopy,
    fileTypeCopy,
    fileRequirementsCopy,
  });
};
